import { HOME_ICONS, type HomeIconName } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Icon } from "@sagot/ui";
import { useId, type KeyboardEvent } from "react";

const K = "deployKnowledge.widgetStudio.home";
const STEP_BY_KEY: Readonly<Record<string, 1 | -1>> = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };

interface IconPickerProps {
  value: HomeIconName;
  onChange: (icon: HomeIconName) => void;
}

/** A radio group of glyphs: one tab stop, arrow keys move and select (WAI-ARIA radio group). */
export function IconPicker({ value, onChange }: IconPickerProps) {
  const { t } = useTranslation();
  const labelId = useId();
  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const step = STEP_BY_KEY[event.key];
    if (!step) return;
    event.preventDefault();
    const next = (index + step + HOME_ICONS.length) % HOME_ICONS.length;
    const icon = HOME_ICONS[next];
    if (!icon) return;
    onChange(icon);
    (event.currentTarget.parentElement?.children[next] as HTMLElement | undefined)?.focus();
  };
  return (
    <div>
      <span id={labelId} className="label">
        {t(`${K}.dialog.icon`)}
      </span>
      <div role="radiogroup" aria-labelledby={labelId} className="grid grid-cols-8 gap-1.5">
        {HOME_ICONS.map((icon, index) => (
          <button
            key={icon}
            type="button"
            role="radio"
            aria-checked={icon === value}
            aria-label={t(`${K}.icons.${icon}`)}
            tabIndex={icon === value ? 0 : -1}
            onClick={() => { onChange(icon); }}
            onKeyDown={(event) => { onKeyDown(event, index); }}
            className="inline-flex size-9 cursor-pointer items-center justify-center rounded-md border border-line text-fg-muted transition-colors hover:border-primary aria-checked:border-primary aria-checked:bg-primary-soft aria-checked:text-primary-ink"
          >
            <Icon name={icon} />
          </button>
        ))}
      </div>
    </div>
  );
}
